import { useEffect, useState } from "react";
import * as React from "react";
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { RoleType } from "../../../App/enums"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { RootState } from "../../../App/store"
import {
    SmallRoomsIcon,
    SmallQuestionIcon,
    VectorUp,
    VectorDown,
    BurgerMenu,
    SmallChatIcon,
    SmallChatIconActive,
    SmallBookmarksIcon,
} from "../Components/SvgIcons"
import TutorialBubble from "../Components/TutorialBubble"
import "../../../Styles/main.scss"
import { setModalData } from "../SharedSlices/ModalSlice";
import { useDispatch } from "react-redux";

function HeaderBurgerMenu() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const translation = useSelector(selectTranslations)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const { id } = useParams()
    const [isQuestionMenuOpen, setIsQuestionMenuOpen] = useState(false)
    const [expanded, setExpanded] = useState(false)
    const [infoExpanded, setInfoExpanded] = useState(false)
    let burgerMenuRef = React.useRef<HTMLInputElement>(null)
    const [burgerMenuOpen, setBurgerMenuOpen] = useState(false)
    const hasUnseenMessages = useSelector((state: RootState) => state.chatSlice.hasUnseenMessages)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const [unseenMessages, setUnseenMessges] = useState(false)

    useEffect(() => {
        setUnseenMessges(hasUnseenMessages)
    }, [hasUnseenMessages])

    const handleStartTutorial = () => {
        setIsQuestionMenuOpen(false)
        setExpanded(false)
        setInfoExpanded(false)
        setBurgerMenuOpen(false)
        dispatch(setModalData({ open: true, showTutorial: true }))
    }

    const onNavigationToMyRooms = () => {
        switch (role) {
            case RoleType.USER:
                navigate(`/all-rooms`)
                window.scrollTo(0, 0)
                break
            case RoleType.ORGANIZATION:
                navigate(`/organization-rooms/${id!}`)
                window.scrollTo(0, 0)
                break
            default:
                break
        }
    }

    const onNavigationToAllRooms = () => {
        switch (role) {
            case RoleType.USER:
                navigate(`/rooms`)
                window.scrollTo(0, 0)
                break
            case RoleType.ORGANIZATION:
                navigate(`/rooms/${id!}`)
                window.scrollTo(0, 0)
                break
            default:
                break
        }
    }

    useEffect(() => {
        const onMouseDownHandler = (event: Event) => {
            const eventTarget = event.target as HTMLElement

            // Ignore tutorial overlay interactions so "Weiter" can advance progress before menu closes.
            if (
                eventTarget?.closest(".speechbubble") ||
                eventTarget?.closest(".speechbubble__backdrop") ||
                eventTarget?.closest(".speechbubble__spotlight")
            ) {
                return
            }

            if (burgerMenuOpen && burgerMenuRef.current && !burgerMenuRef.current.contains(eventTarget)) {
                setBurgerMenuOpen(false)
            }
        }
        document.addEventListener("mousedown", onMouseDownHandler)
    })

    return (
        <div ref={burgerMenuRef}>
            {role === RoleType.USER && (
                <TutorialBubble text={translation.tutorial_user_roomlist} placement="left" visible={userDataState.tutorial_progress === 1} hasNext={true} />
            )}
            <div data-tutorial-anchor="header-burger-toggle" onClick={() => setBurgerMenuOpen(!burgerMenuOpen)}>
                <BurgerMenu />
            </div>
            {burgerMenuOpen && (
                <>
                    <div className="header-burger-menu">

                        <div
                            onClick={() => setExpanded(!expanded)}
                            data-tutorial-anchor="header-burger-rooms-toggle"
                            className={`header-burger-menu__item ${expanded && "header-burger-menu__item--rooms-dropdown"}`}
                        >
                            <div className="header-burger-menu__icon">
                                <SmallRoomsIcon />
                            </div>
                            <div className="header-burger-menu__text header-burger-menu__text--rooms-dropdown">{translation.rooms}</div>
                            {expanded ? (
                                <div className="header-burger-menu__icon">
                                    <VectorUp />
                                </div>
                            ) : (
                                <div className="header-burger-menu__icon">
                                    <VectorDown />
                                </div>
                            )}
                        </div>
                        {expanded && (
                            <div className="header-burger-menu__rooms-dropdown-content">
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={onNavigationToMyRooms}>
                                    {translation.myRooms}
                                </p>
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={onNavigationToAllRooms}>
                                    {translation.exploreRooms}
                                </p>
                            </div>
                        )}
                        <div data-tutorial-anchor="header-chat-mobile" onClick={() => navigate(`/chat`)} className="header-burger-menu__item">
                            <div className="header-burger-menu__icon">{!unseenMessages ? <SmallChatIcon /> : <SmallChatIconActive />}</div>
                            <div className="header-burger-menu__text">{translation.directChat}</div>
                        </div>
                        <div
                            onClick={() => setInfoExpanded(!infoExpanded)}
                            data-tutorial-anchor="header-burger-info-toggle"
                            className={`header-burger-menu__item ${infoExpanded && "header-burger-menu__item--rooms-dropdown"}`}
                        >
                            <div className="header-burger-menu__icon">
                                <SmallBookmarksIcon />
                            </div>
                            <div className="header-burger-menu__text header-burger-menu__text--rooms-dropdown">{translation.information || "Informationen"}</div>
                            {infoExpanded ? (
                                <div className="header-burger-menu__icon">
                                    <VectorUp />
                                </div>
                            ) : (
                                <div className="header-burger-menu__icon">
                                    <VectorDown />
                                </div>
                            )}
                        </div>
                        {infoExpanded && (
                            <div className="header-burger-menu__rooms-dropdown-content">
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={() => window.open("https://edusiia.com/ueber-uns/", "_blank")}>
                                    {translation.aboutUs}
                                </p>
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={() => window.open("https://edusiia.com/unsere-werte/", "_blank")}>
                                    {translation.ourValues}
                                </p>
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={() => window.open("https://edusiia.com/veranstaltungen/", "_blank")}>
                                    {translation.eventCalendar}
                                </p>
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={() => window.open("https://edusiia.com/presse/", "_blank")}>
                                    {translation.supportEdusiia}
                                </p>
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={() => window.open("https://edusiia.com/nutzungsbedingungen/", "_blank")}>
                                    {translation.termsOfUse}
                                </p>
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={() => window.open("https://edusiia.com/datenschutz/", "_blank")}>
                                    {translation.privacy}
                                </p>
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={() => window.open("https://edusiia.com/impressum/", "_blank")}>
                                    {translation.impressum}
                                </p>
                            </div>
                        )}

                        <div
                            onClick={() => setIsQuestionMenuOpen(!isQuestionMenuOpen)}
                            data-tutorial-anchor="header-burger-help-toggle"
                            className={`header-burger-menu__item ${isQuestionMenuOpen && "header-burger-menu__item--rooms-dropdown"}`}
                        >
                            <div className="header-burger-menu__icon">
                                <SmallQuestionIcon />
                            </div>
                            <div className="header-burger-menu__text header-burger-menu__text--rooms-dropdown">{translation.help || "Hilfe"}</div>
                            {isQuestionMenuOpen ? (
                                <div className="header-burger-menu__icon">
                                    <VectorUp />
                                </div>
                            ) : (
                                <div className="header-burger-menu__icon">
                                    <VectorDown />
                                </div>
                            )}
                        </div>
                        {isQuestionMenuOpen && (
                            <div className="header-burger-menu__rooms-dropdown-content">
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={handleStartTutorial}>
                                    {translation.onboarding}
                                </p>
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={() => window.open("https://edusiia.com/faq/", "_blank")}>
                                    {translation.questionAndAnswers}
                                </p>
                                <p className="header-burger-menu__rooms-dropdown-item" onClick={() => window.open("https://edusiia.com/kontakt/", "_blank")}>
                                    {translation.contact}
                                </p>
                            </div>
                        )}

                    </div>
                </>
            )}{" "}
        </div>
    )
}

export default HeaderBurgerMenu
